/**
 * Expert Notes (Capability Assessment) component that fetches its own data.
 * Uses per-section API endpoints for better performance.
 * Requires core data (expertNotes, generalNotes) + metadata (recommendedUses, recommendedDomains).
 */
import { FieldProvenanceDto, MissionAssessmentFieldName } from '@@types/api.types';
import { expertNotesConfig } from '@components/pages/mission-assessment/components/mission-assessment-form-notes.component';
import { FieldProvenanceBadge } from '@components/shared/field-provenance-badge.component';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@components/ui/accordion';
import {
  useMissionAssessmentCoreQueryOptions,
  useMissionAssessmentMetadataQueryOptions,
} from '@queries/mission-assessments.query';
import { useQuery } from '@tanstack/react-query';
import { type FunctionComponent, useMemo } from 'react';
import { MissionAssessmentCoreSkeleton } from './mission-assessment-skeletons.component';
import { TechnologyMissionAssessmentRecommendedTechUses } from './technology-mission-assessment-recommended-tech-uses.component';
import { TechnologyMissionAssessmentRecommendedUsesAndDomains } from './technology-mission-assessment-recommended-uses-and-domains.component';

type TechnologyMissionAssessmentExpertNotesProps = {
  missionAssessmentId: string;
};

type NoteSection = {
  content: string;
  provenance?: FieldProvenanceDto | null;
  title: string;
};

export const TechnologyMissionAssessmentExpertNotesComponent: FunctionComponent<
  TechnologyMissionAssessmentExpertNotesProps
> = (props) => {
  const { missionAssessmentId } = props;

  const { data: coreData, isLoading: isCoreLoading } = useQuery(
    useMissionAssessmentCoreQueryOptions(missionAssessmentId),
  );
  const { data: metadataData, isLoading: isMetadataLoading } = useQuery(
    useMissionAssessmentMetadataQueryOptions(missionAssessmentId),
  );

  const isLoading = isCoreLoading || isMetadataLoading;

  const sections = useMemo<NoteSection[]>(() => {
    if (!coreData?.data) return [];

    const provenance = coreData.provenance ?? undefined;

    const expertNoteSections =
      coreData.data.expertNotes
        ?.filter((expertNote) => expertNotesConfig.some((config) => config.identifier === expertNote.identifier))
        ?.map((expertNote) => {
          const config = expertNotesConfig.find((c) => c.identifier === expertNote.identifier);
          return {
            content: expertNote.content,
            provenance: provenance?.[expertNote.identifier] ?? null,
            title: config?.title || '',
          };
        }) ?? [];

    if (coreData.data.generalNotes) {
      expertNoteSections.push({
        content: coreData.data.generalNotes,
        provenance: provenance?.[MissionAssessmentFieldName.GENERAL_NOTES] ?? null,
        title: 'General Notes',
      });
    }

    return expertNoteSections;
  }, [coreData?.data, coreData?.provenance]);

  if (isLoading) {
    return <MissionAssessmentCoreSkeleton />;
  }

  const recommendedTechnologyUses = metadataData?.recommendedTechnologyUses ?? [];
  // Deprecated fields - kept for backwards compatibility
  const recommendedDomains = metadataData?.recommendedDomains ?? [];
  const recommendedUses = metadataData?.recommendedUses ?? [];

  // Use new structure if available, otherwise fall back to deprecated fields
  const hasNewStructure = recommendedTechnologyUses.length > 0;
  const hasDeprecatedData = recommendedUses.length > 0 || recommendedDomains.length > 0;

  return (
    <section>
      <div className="flex flex-col gap-y-3 mt-2">
        {sections.length > 0 ? (
          <Accordion
            className="space-y-3"
            type="multiple"
          >
            {sections.map((section, index) => (
              <AccordionItem
                className="border rounded-lg bg-white border-gray-200"
                key={`${section.title}-${index}`}
                value={`section-${index}`}
              >
                <AccordionTrigger className="px-4 hover:no-underline text-sm font-medium text-gray-700">
                  <span className="flex items-center gap-2">
                    {section.title}
                    <FieldProvenanceBadge provenance={section.provenance} />
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <p className="text-sm text-gray-600 leading-relaxed">{section.content}</p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        ) : (
          <p className="text-xs text-gray-500">No data available</p>
        )}
      </div>

      {/* Recommended Technology Uses (new structure) */}
      {hasNewStructure && (
        <TechnologyMissionAssessmentRecommendedTechUses
          provenance={coreData?.provenance}
          recommendedTechnologyUses={recommendedTechnologyUses}
        />
      )}

      {/* Recommended Uses and Domains (deprecated - shown only if no new structure) */}
      {!hasNewStructure && hasDeprecatedData && (
        <TechnologyMissionAssessmentRecommendedUsesAndDomains
          recommendedDomains={recommendedDomains}
          recommendedUses={recommendedUses}
        />
      )}
    </section>
  );
};
